// Briefings — list + detail with IntakePane.
//
// List view: card-grid layout matching the editorial "Projects" mock.
// Top row: title + "New briefing" pill button. Search input below
// (client-side title/subtitle filter). Filter chips + sort dropdown
// on a single row. Card grid (2-col on desktop) with title /
// subtitle / status / "Updated …" footer.
//
// Detail: when a briefing has no marketing brief yet (status: draft),
// shows the IntakePane chat surface so the CD agent can run intake.

import { ChevronDown, Plus, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { BriefingWorkspaceLayout } from "@/components/BriefingWorkspaceLayout";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { useStore } from "@/lib/store";
import type { BriefingFilter, BriefingSummary } from "@/lib/types";
import { cn } from "@/lib/utils";

const STATUS_FILTERS: Array<{
  label: string;
  value: BriefingFilter["status"];
}> = [
  { label: "All", value: null },
  { label: "Active", value: "draft,in_creative_review,in_business_review" },
  { label: "Draft", value: "draft" },
  { label: "In creative review", value: "in_creative_review" },
  { label: "In business review", value: "in_business_review" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
];

function relTime(iso?: string): string {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function BriefingsPage() {
  const { route } = useStore();
  if (route.briefingId) {
    return <BriefingDetail briefingId={route.briefingId} />;
  }
  return <BriefingsList />;
}

type SortKey = "created" | "updated" | "title";

const SORT_LABELS: Record<SortKey, string> = {
  created: "Created",
  updated: "Updated",
  title: "Title",
};

function sortBriefings(rows: BriefingSummary[], key: SortKey): BriefingSummary[] {
  // Don't mutate the array the store handed us — clone first.
  const out = [...rows];
  if (key === "title") {
    out.sort((a, b) =>
      (a.title || "").localeCompare(b.title || "", undefined, {
        sensitivity: "base",
      })
    );
  } else if (key === "updated") {
    out.sort(
      (a, b) =>
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    );
  } else {
    out.sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }
  return out;
}

function BriefingsList() {
  const {
    briefings,
    briefingFilter,
    setBriefingFilter,
    createBriefing,
    navigate,
  } = useStore();

  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("created");
  const [sortOpen, setSortOpen] = useState(false);

  // Client-side text filter on title + subtitle. Status filter is
  // already applied server-side via briefingFilter.
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? briefings.filter((b) =>
          [(b.title || ""), b.subtitle || ""]
            .join(" ")
            .toLowerCase()
            .includes(q)
        )
      : briefings;
    return sortBriefings(filtered, sortKey);
  }, [briefings, search, sortKey]);

  return (
    <div className="mx-auto w-full max-w-5xl px-6 md:px-10 py-12 space-y-6">
      {/* Header — title + new-briefing pill */}
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-3xl font-semibold tracking-tight">Briefings</h1>
        <Button
          onClick={() => createBriefing()}
          shape="pill"
          size="sm"
          className="gap-1.5"
        >
          <Plus className="h-3.5 w-3.5" />
          New briefing
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/70 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search briefings…"
          className={cn(
            "w-full rounded-full border border-border bg-card",
            "pl-10 pr-4 py-2.5 text-sm",
            "placeholder:text-muted-foreground/70",
            "outline-none transition-shadow",
            "focus-visible:ring-2 focus-visible:ring-ring/30"
          )}
        />
      </div>

      {/* Filter chips + sort dropdown */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.label}
              onClick={() =>
                setBriefingFilter({ ...briefingFilter, status: f.value })
              }
              className={cn(
                "rounded-full border px-3 py-1.5 text-xs transition-colors",
                briefingFilter.status === f.value
                  ? "border-foreground bg-foreground text-background"
                  : "border-border bg-background hover:bg-accent"
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
        <SortDropdown
          value={sortKey}
          onChange={setSortKey}
          open={sortOpen}
          onOpenChange={setSortOpen}
        />
      </div>

      {/* Card grid */}
      {visible.length === 0 ? (
        <EmptyState search={search} briefingCount={briefings.length} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {visible.map((b) => (
            <BriefingCard
              key={b.id}
              briefing={b}
              onClick={() => navigate(`/briefings/${b.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function BriefingCard({
  briefing,
  onClick,
}: {
  briefing: BriefingSummary;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "group text-left rounded-xl border border-border bg-card",
        "p-5 flex flex-col gap-3 min-h-[150px]",
        "transition-all hover:border-foreground/20 hover:shadow-card",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-semibold leading-snug line-clamp-1 flex-1 min-w-0">
          {briefing.title || "Untitled briefing"}
        </p>
        <StatusPill status={briefing.status} size="sm" />
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 flex-1">
        {briefing.subtitle || "No description yet."}
      </p>
      <p className="text-xs text-muted-foreground/70 mt-auto">
        Updated {relTime(briefing.updated_at)}
      </p>
    </button>
  );
}

function SortDropdown({
  value,
  onChange,
  open,
  onOpenChange,
}: {
  value: SortKey;
  onChange: (next: SortKey) => void;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  // Click-outside handler — closes the dropdown when the user clicks
  // anywhere else on the page. Light-weight; no portal / outside-click
  // hook needed at this scale.
  useEffect(() => {
    if (!open) return;
    const onDoc = () => onOpenChange(false);
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, [open, onOpenChange]);

  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <span>Sort by</span>
      <div className="relative" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={() => onOpenChange(!open)}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md border border-border bg-card",
            "px-2.5 py-1.5 text-xs text-foreground hover:bg-accent transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
          )}
        >
          {SORT_LABELS[value]}
          <ChevronDown className="h-3 w-3" />
        </button>
        {open && (
          <div
            className={cn(
              "absolute right-0 top-full mt-1 min-w-[140px] z-10",
              "rounded-md border border-border bg-popover shadow-md p-1"
            )}
          >
            {(Object.keys(SORT_LABELS) as SortKey[]).map((key) => (
              <button
                key={key}
                onClick={() => {
                  onChange(key);
                  onOpenChange(false);
                }}
                className={cn(
                  "w-full text-left rounded px-2.5 py-1.5 text-xs",
                  "hover:bg-accent",
                  value === key && "bg-accent font-medium"
                )}
              >
                {SORT_LABELS[key]}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyState({
  search,
  briefingCount,
}: {
  search: string;
  briefingCount: number;
}) {
  const searching = search.trim().length > 0;
  return (
    <div className="rounded-xl border border-dashed border-border bg-card/40 p-12 text-center">
      <p className="text-sm text-muted-foreground italic">
        {searching
          ? `No briefings match "${search}".`
          : briefingCount === 0
            ? "No briefings on file. Begin one above."
            : "No briefings match the current filter."}
      </p>
    </div>
  );
}

function BriefingDetail({ briefingId }: { briefingId: string }) {
  const { briefingsById, loadBriefing } = useStore();
  const briefing = briefingsById[briefingId];
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    loadBriefing(briefingId);
  }, [briefingId, refreshKey, loadBriefing]);

  if (!briefing) {
    return (
      <div className="mx-auto w-full max-w-[1200px] px-6 md:px-10 py-12">
        <div className="text-sm text-muted-foreground">Loading briefing…</div>
      </div>
    );
  }

  return (
    <BriefingWorkspaceLayout
      briefing={briefing}
      onRefetch={() => setRefreshKey((k) => k + 1)}
    />
  );
}
